import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import PanelTemplate from "../../templates/Panel";
import PageHeader from "../../components/PageHeader";
import Box from "../../components/Box";
import Table from "../../components/Table";
import Button from "../../components/Button";
import Icon from "../../components/Icon";
import { ModalDelete } from "../../components/ModalDelete";

import api from "../../services/api";
import { renderError } from "../../helpers/errors";
import { canUseGestao, hasPermission } from "../../services/auth";

/**
 * Telas próprias que entram na prateleira de prontos (BDM-888). O DRE não cabe
 * no motor de fontes curadas: tem regime de competência/caixa, hierarquia do
 * plano de contas e análise vertical. Em vez de duplicá-lo como relatório
 * salvo, o card leva para a tela dele — quem procura relatório procura aqui.
 */
const NATIVE_REPORTS = [
    {
        key: 'income-statement',
        name: 'DRE',
        description: 'Resultado do período por regime de competência ou de caixa, com análise vertical.',
        dataset: 'Financeiro',
        to: '/finance/income-statement',
        show: () => canUseGestao() && hasPermission(['view_finance', 'manage_finance']),
    },
];

/**
 * Lista do módulo de relatórios (BDM-1040).
 *
 * Prontos e salvos vêm da mesma tabela e do mesmo endpoint — a separação em
 * dois blocos é só de leitura. Duplicar um pronto é o caminho de entrada do
 * construtor: ninguém precisa começar de uma tela em branco.
 */
export const ReportList = () => {
    const [loading, setLoading] = useState(true);
    const [reports, setReports] = useState([]);
    const [datasets, setDatasets] = useState([]);
    const [search, setSearch] = useState('');
    const [reportToDelete, setReportToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const navigate = useNavigate();

    useEffect(() => {
        getReports();
        api.get(`/report/dataset`).then(res => setDatasets(res.data.resources)).catch(() => {});
    }, []);

    const getReports = () => {
        setLoading(true);

        api.get(`/report`)
            .then(res => setReports(res.data.resources))
            .catch(renderError)
            .then(() => setLoading(false));
    };

    const datasetLabel = key => datasets.find(dataset => dataset.key === key)?.label ?? key;

    const matches = report => {
        const term = search.trim().toLowerCase();
        if (term === '') return true;

        return `${report.name} ${report.description ?? ''}`.toLowerCase().includes(term);
    };

    const templates = useMemo(
        () => reports.filter(report => report.is_template && matches(report)),
        [reports, search]
    );

    const natives = useMemo(
        () => NATIVE_REPORTS.filter(report => report.show() && matches(report)),
        [search]
    );

    const custom = useMemo(
        () => reports.filter(report => !report.is_template && matches(report)),
        [reports, search]
    );

    const duplicate = report => {
        api.post(`/report/${report.id}/duplicate`)
            .then(res => {
                toast.success('Cópia criada — ajuste o que precisar');
                navigate(`/report/${res.data.resource.id}/edit`);
            })
            .catch(renderError);
    };

    const remove = () => {
        setDeleting(true);

        api.delete(`/report/${reportToDelete.id}`)
            .then(() => {
                toast.success('Relatório excluído');
                setReportToDelete(null);
                getReports();
            })
            .catch(renderError)
            .then(() => setDeleting(false));
    };

    const customTable = {
        header: ['Nome', 'Fonte de dados', 'Criado por', 'Última execução', ''],
        rows: custom.map(report => ({
            data: [
                report.name,
                datasetLabel(report.dataset),
                report.admin?.name ?? '—',
                report.last_run_at ? moment(report.last_run_at).format('DD/MM/YYYY HH:mm') : 'Nunca',
                <div className="report-row-actions">
                    <Button type={`secondary`} size={`small`} text={`Abrir`} action={() => navigate(`/report/${report.id}`)} />
                    <Button type={`secondary`} size={`small`} text={`Duplicar`} action={() => duplicate(report)} />
                    <span title={`Excluir relatório`}>
                        <Button type={`delete`} svg={`delete`} action={() => setReportToDelete(report)} />
                    </span>
                </div>,
            ],
            action: () => navigate(`/report/${report.id}`),
        })),
    };

    return (
        <PanelTemplate id="report-list">
            <PageHeader
                left={
                    <div>
                        <h1>Relatórios</h1>
                        <span className="report-subtitle">
                            Comece por um pronto ou monte o seu escolhendo campos, filtros e período.
                        </span>
                    </div>
                }
                right={
                    <>
                        <div className="filter-simple has-input">
                            <input
                                type="text"
                                placeholder="Buscar relatório"
                                value={search}
                                onChange={event => setSearch(event.target.value)}
                            />
                        </div>
                        {datasets.length > 0 &&
                            <Button
                                type={`primary`}
                                size={`small`}
                                text={`Novo relatório`}
                                svg={`plus-white`}
                                action={() => navigate(`/report/new`)}
                            />
                        }
                    </>
                }
            />

            {!loading && datasets.length === 0 &&
                <div className="box-alert-info">
                    Nenhuma fonte de dados disponível no seu plano ou no seu nível de acesso.
                </div>
            }

            {(templates.length > 0 || natives.length > 0) &&
                <Box title={`Relatórios prontos`}>
                    <div className="report-template-grid">
                        {natives.map(report => (
                            <div key={report.key} className="report-template-card" onClick={() => navigate(report.to)}>
                                <div className="card-title">
                                    <span className="card-icon"><Icon name="chart" /></span>
                                    <strong>{report.name}</strong>
                                </div>
                                <span className="card-description">{report.description}</span>
                                <span className="card-dataset">{report.dataset}</span>
                            </div>
                        ))}
                        {templates.map(report => (
                            <div key={report.id} className="report-template-card" onClick={() => navigate(`/report/${report.id}`)}>
                                <div className="card-title">
                                    <span className="card-icon"><Icon name="chart" /></span>
                                    <strong>{report.name}</strong>
                                </div>
                                <span className="card-description">{report.description}</span>
                                <span className="card-dataset">{datasetLabel(report.dataset)}</span>
                            </div>
                        ))}
                    </div>
                </Box>
            }

            <Box title={`Meus relatórios`} className={`no-padding`}>
                {custom.length === 0 && !loading
                    ? <div className="report-result-message">
                        Você ainda não salvou nenhum relatório. Abra um pronto e clique em Duplicar para começar do jeito mais rápido.
                    </div>
                    : <Table header={customTable.header} rows={customTable.rows} loading={loading} />
                }
            </Box>

            <ModalDelete
                show={!!reportToDelete}
                action={remove}
                loading={deleting}
                close={() => setReportToDelete(null)}
                text={reportToDelete ? `Deseja realmente excluir o relatório "${reportToDelete.name}"?` : ''}
            />
        </PanelTemplate>
    );
};

export default ReportList;
